import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Mail, BookOpen } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface HelpSupportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpSupportModal({ open, onOpenChange }: HelpSupportModalProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = () => {
    if (!subject.trim() || !message.trim()) {
      toast({ title: "Missing Fields", description: "Please fill in both subject and message.", variant: "destructive" });
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSubject("");
      setMessage("");
      toast({ title: "Message Sent", description: "Our support team will respond within 24 hours." });
      onOpenChange(false);
    }, 800);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Help & Support</DialogTitle>
        </DialogHeader>

        {/* Quick Links */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="hover:border-primary/30 cursor-pointer transition-colors">
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium text-foreground">Live Chat</span>
              <span className="text-[10px] text-muted-foreground">Coming soon</span>
            </CardContent>
          </Card>
          <Card className="hover:border-primary/30 cursor-pointer transition-colors">
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium text-foreground">Email</span>
              <span className="text-[10px] text-muted-foreground">support@recom.co</span>
            </CardContent>
          </Card>
          <Card className="hover:border-primary/30 cursor-pointer transition-colors">
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="text-xs font-medium text-foreground">Docs</span>
              <span className="text-[10px] text-muted-foreground">Browse guides</span>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <div className="space-y-3 pt-2">
          <p className="text-sm font-medium text-foreground">Send us a message</p>
          <div className="space-y-2">
            <Label htmlFor="help-subject">Subject</Label>
            <Input
              id="help-subject"
              placeholder="Brief description of your issue..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="help-message">Message</Label>
            <Textarea
              id="help-message"
              placeholder="Describe your question or issue in detail..."
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={sending} size="sm">
              {sending ? "Sending..." : "Send Message"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
